import "core-js/modules/es6.array.iterator";
import "core-js/modules/es6.object.to-string";
import "core-js/modules/es6.object.keys";
import "core-js/modules/es7.symbol.async-iterator";
import "core-js/modules/es6.symbol";
import "core-js/modules/web.dom.iterable";
import _isEqual from "lodash/isEqual";

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty(target, key, source[key]); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance"); }

function _iterableToArrayLimit(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import { useState, useContext, useRef, useEffect } from 'react';
import uuid from 'uuid/v4';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import AnalyticsContext from '../AnalyticsContext';
import ImpressionContext from './ImpressionContext';
import { useWait } from '../hooks';
/**
 * Hook that will fire an event when `isVisible` has been true for more than
 * 200ms. This hook is designed to be agnostic to how `isVisible` is determined
 * or where the analytics instance comes from. It should not be used directly;
 * web and mobile have their own specialized wrappers that automatically
 * provide `isVisible`. See `useImpression.js` and `useImpression.native.js`.
 *
 * This hook will automatically refire after an event's payload has changed.
 * This can happen in one of two ways:
 *
 * 1. `eventType`, `entityName`, `elementName`, and/or `payload` props change.
 * 2. The page's `navigation_id` changes during a transition from
 *    "visible" -> "not visible" -> "visible". The most common scenario in
 *    which this will happen is in the mobile app when a user navigates back to
 *    a page that had previously fired an event, since components on screens in
 *    the back stack are generally not unmounted.
 *
 * In case this behavior is not desirable, this can be toggled off with the
 * `refireOnEventChange` flag. If this flag is set to false, this hook is
 * guaranteed to only ever fire at most one event.
 */

var useImpressionImpl = function useImpressionImpl(_ref) {
  var _ref$eventType = _ref.eventType,
      eventType = _ref$eventType === void 0 ? 'element_impression' : _ref$eventType,
      elementName = _ref.elementName,
      entityName = _ref.entityName,
      _ref$payload = _ref.payload,
      payload = _ref$payload === void 0 ? {} : _ref$payload,
      _ref$refireOnEventCha = _ref.refireOnEventChange,
      refireOnEventChange = _ref$refireOnEventCha === void 0 ? true : _ref$refireOnEventCha,
      _ref$isVisible = _ref.isVisible,
      isVisible = _ref$isVisible === void 0 ? false : _ref$isVisible;

  // Don't generate a UUID immediately; wait until after the first render.
  // This ensures that there won't be a DOM mismatch during React hydration
  // if this ID ends up somewhere in the DOM (for instance, in an `href`).
  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      impressionId = _useState2[0],
      setImpressionId = _useState2[1]; // Pull the analytics instance from Context.


  var analytics = useContext(AnalyticsContext); // If there's an Impression component above us in the component tree, we'll
  // consume its impressionId and use it for the `parent_impression_id`
  // property on the payload.

  var parentImpression = useContext(ImpressionContext) || {}; // We use `useRef` instead of `useState` because we don't need to rerender
  // after an event is fired, and also because React doesn't guarantee that a
  // component will always render with the latest state. If we used `useState`
  // instead, we could end up with duplicate events.

  var previousEvent = useRef(null); // Compute the payload ahead of time - this is what we'll use to decide if
  // we need to refire an event. If the payload is different from the
  // previously-fired event (possibly "null" if no event has been fired yet),
  // we need to fire a new event.

  var newEvent = {
    eventType: eventType,
    payload: _objectSpread({
      parent_impression_id: parentImpression.impressionId,
      element_name: elementName,
      entity_name: entityName,
      // Normally this would be provided by the `impression` event decorator,
      // but we need to explicitly include it here so that we can correctly
      // diff payloads.
      navigation_id: analytics.getState('navigation_id')
    }, payload)
  }; // We'll stringify the constructed event so that we can determine when it
  // changes in `useEffect` dependencies - if we just passed the whole object,
  // `useEffect` would only do a shallow comparison.
  //
  // IMPORTANT: when checking for when we need to generate a new impression ID,
  // we can't include the impression ID in the event payload. Since changing the
  // contents of the `newEvent` object will trigger a new ID to be generated
  // (see below), if we include that ID in `newEvent`, we'll end up regenerating
  // the ID every time we regenerate the ID, which is a delightfully infinite
  // loop of regeneration.

  var stringifiedNewEventWithoutImpressionId = JSON.stringify(newEvent);
  newEvent.payload.impression_id = impressionId; // The below `useEffect` will generate an impression ID after the first render.
  //
  // Also...
  // The Data folks expect each impression even we fire to include a unique
  // `impression_id`. However, we can't just generate this ID on the fly at
  // the instant we fire the event. Because we use React Context to pass IDs
  // to children for use as `parent_impression_id`, we need to know the UUID
  // in advance of the event actually firing. So, whenever we detect that we
  // *might* be firing a new event soon (i.e. `newEvent` changes), we'll
  // generate a new UUID and store it in our state so that React will
  // re-render and correctly propagate the ID to our potential children.
  //
  // Note that we have to take into account `refireOnEventChange` here.
  // Because the impression ID is returned to (and possibly used by) the caller,
  // we need to make sure this impression ID is stable for the life of this hook
  // if `refireOnEventChange` is disabled.
  //
  // IMPORTANT: we don't re-generate a new ID when `isVisible` changes. Because
  // generating a new ID could cause the event to be refired, after we generate
  // the initial ID, we only re-generate an ID if we know we would otherwise
  // re-fire the event, which could only happen if `newEvent` changed.

  var previousStringifiedNewEventWithoutImpressionId = useRef(null);
  var previousImpressionId = useRef(null);
  useEffect(function () {
    if (!refireOnEventChange && previousImpressionId.current !== null || previousStringifiedNewEventWithoutImpressionId.current === stringifiedNewEventWithoutImpressionId) {
      return;
    }

    var newId = uuid();
    setImpressionId(newId);
  }, [refireOnEventChange, stringifiedNewEventWithoutImpressionId]); // Unlike the above `useEffect`, we want this hook to fire even when the
  // `impression_id` changes. To do that, we'll just tack on `impressionId` to
  // the stringified event. The value doesn't have to be a valid JSON string -
  // it just needs to be something that's guaranteed to change when the event does.

  var stringifiedEventWithImpressionId = "".concat(stringifiedNewEventWithoutImpressionId, ":").concat(impressionId);
  useWait([isVisible, refireOnEventChange, stringifiedEventWithImpressionId], 200, function () {
    // This shouldn't ever happen, but if we're running on the client and
    // haven't yet hit the `useEffect` to generate a UUID, don't fire an
    // event yet. When the UUID is generated, we'll end up here again shortly.
    if (impressionId === null) return; // This conditional covers the two potential cases of
    // `refireOnEventChange`:
    // * If it should refire on event change, fire an event if the new event
    //   does not match the previously fired event.
    // * If it should not refire on event change, fire an event only if no
    //   event was previously fired.

    if (isVisible && (refireOnEventChange && !_isEqual(newEvent, previousEvent.current) || !refireOnEventChange && previousEvent.current === null)) {
      // This handles an edge case where the navigation_id is updated between
      // when `newEvent` is computed and when the event actually fires, which
      // occurs because of the 200ms delay between those two steps.
      newEvent.payload.navigation_id = analytics.getState('navigation_id');
      analytics.track(newEvent.eventType, newEvent.payload);
      previousEvent.current = newEvent;
    }
  }); // prefer `entityName` over `elementName`

  useDeprecatedPropsWarning({
    component: 'useImpression',
    deprecatedProps: [{
      version: '1.19.0',
      deprecated: 'elementName',
      migration: 'Use entityName instead.'
    }],
    props: {
      elementName: elementName
    }
  });
  return {
    impressionId: impressionId
  };
};

export default useImpressionImpl;